import React, {useEffect, useState} from 'react';
import {gql, ApolloError} from '@apollo/client';
import {Redirect} from 'react-router-dom';
import {FeatureFlagsProvider, createFeatureFlagsClient} from '@ezcater/feature-flags-react';
import {ONBOARDING_PATH, signInUrlWithDestUrl} from '../../paths';
import Loader from '../Loader';
import CurrentAccountContext from './CurrentAccountContext';
import {useEnvironment} from '../Environment';
import GeneralError from '../GeneralError';
import {RequireAuthentication as RequireAuthenticationIdentityType} from '../../__generated__/RequireAuthentication';
import OnboardingRedirect from './OnboardingRedirect';

interface RequiredAuthenticationProps {
  loggedOutListener: (listener: () => void) => void;
  requestUrl: string;
  children: React.ReactNode;
  me?: RequireAuthenticationIdentityType | null;
  loading: boolean;
  error?: ApolloError;
  requireFullAccess?: boolean;
}

const RequireAuthentication = ({
  loggedOutListener,
  requestUrl,
  children,
  loading,
  error,
  me,
  requireFullAccess = true,
}: RequiredAuthenticationProps) => {
  const [becameUnauthorized, setBecameUnauthorized] = useState(false);

  const onBecomeUnauthorized = () => {
    setBecameUnauthorized(true);
  };

  const userId = me?.catererAccount?.trackingId;
  useEffect(() => {
    if (userId && window && (window as any).DD_RUM) {
      (window as any).DD_RUM.onReady(() => {
        (window as any).DD_RUM.setUser({
          id: userId,
        });
      });
    }
  }, [userId]);

  useEffect(() => {
    loggedOutListener(onBecomeUnauthorized);
  }, [loggedOutListener]);

  const performLoginRedirect = () => {
    if (typeof window !== 'undefined') {
      // On the client, Redirect doesn't work with absolute urls. As a
      // workaround, use window.location.assign to perform the redirect
      // and render the Loader until the redirect actually happens.

      const currentLocation = window.location.href;
      window.location.assign(signInUrlWithDestUrl(currentLocation));

      return <Loader inline={false} />;
    }

    return <Redirect to={signInUrlWithDestUrl(requestUrl)} />;
  };

  const {featureFlagHost} = useEnvironment();
  const featureFlagsClient = createFeatureFlagsClient({
    host: featureFlagHost,
  });

  if (becameUnauthorized) {
    return performLoginRedirect();
  }

  if (loading) {
    return <Loader inline={false} />;
  }

  if (me?.catererAccount?.id && (me.catererAccount.hasFullAccess || !requireFullAccess)) {
    const currentAccount = me.catererAccount;
    const allowlistValues = currentAccount.brands.map(brand => `brand:${brand.id}`);

    return (
      <CurrentAccountContext.Provider value={currentAccount}>
        <FeatureFlagsProvider
          client={featureFlagsClient}
          trackingId={currentAccount.id}
          allowlistValues={allowlistValues}
        >
          <OnboardingRedirect
            isOnboarding={currentAccount.isOnboarding}
            onOnboardingPage={
              !!requestUrl.includes(ONBOARDING_PATH) ||
              (typeof window !== 'undefined' &&
                window?.location?.pathname?.includes(ONBOARDING_PATH))
            }
          >
            {children}
          </OnboardingRedirect>
        </FeatureFlagsProvider>
      </CurrentAccountContext.Provider>
    );
  }

  // We don't want to redirect if we didn't get a response back due to network error
  // Reason being that the user may actually be logged in and then they get into a redirect
  // loop (assuming network errors keep happening for instance due to a cloudflare country block )
  if (error?.networkError) {
    return <GeneralError />;
  }

  return performLoginRedirect();
};

// Be careful adding any additional calls to this query for data that may be behind the full access guard
// If adding somethng behind that guard an unwanted redirect to log in will occur for any anonymous user
export const requireAuthenticationFragment = gql`
  fragment RequireAuthentication on Identity {
    id
    catererAccount {
      id
      trackingId
      email
      name
      hasFullAccess
      isOnboarding
      brands {
        id
      }
    }
  }
`;

export default RequireAuthentication;
